import _extends from "@babel/runtime/helpers/extends";
/* eslint-disable react/jsx-no-duplicate-props */
import FormControlLabel from "@material-ui/core/esm/FormControlLabel";
import Radio from "@material-ui/core/esm/Radio";
import RadioGroup from "@material-ui/core/esm/RadioGroup";
import TextField from "@material-ui/core/esm/TextField";
import { MuiTelInput } from 'mui-tel-input';
import { nanoid } from 'nanoid';
import React, { useEffect, useRef, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormElement, InputAdornment } from 'components/Form';
import { Title } from 'components/Text';
import { DEFAULT_LANGUAGE } from 'constants/language';
import { norwegianOrganizationNumberRegex } from 'constants/regex';
import useGetValidationString from 'hooks/language/useGetValidationString';
import showEmailError from 'utils/form/showEmailError';
import showPhoneNumberError from 'utils/form/showPhoneNumberError';
import isNorwegian from 'utils/language/isNorwegian';
import getPhoneNumberError from 'utils/phone/getPhoneNumberError';
import isValidPhoneNumber from 'utils/phone/isValidPhoneNumber';
import useStyles from '../CreateCustomerStyles';
import { CustomersService } from 'api';
import { useSetRecoilState } from 'recoil';
import { isOrgNumberValid } from 'api/customers/getCustomer';
const idSuffix = nanoid();
export default function CreateCustomerPersonalia() {
  const {
    t,
    i18n
  } = useTranslation();
  const {
    textField
  } = useStyles();
  const isNorwegianLangSet = isNorwegian(i18n);
  const {
    register,
    errors,
    watch,
    trigger,
    control
  } = useFormContext();
  const {
    emailFormat,
    required,
    norwegianOrganizationNumber,
    organizationNumberExists
  } = useGetValidationString();

  // Email validation
  const email = watch('email');
  const contactEmail = watch('contactEmail');
  const validateEmail = input => {
    if (!input) return true;
    return !showEmailError(input);
  };
  const [orgNrErrorMessage, setOrgNrErrorMessage] = useState();
  const [takenOrgNumber, setTakenOrgNumber] = useState(true);
  const orgNumber = watch('organizationNumber');
  const setOrgNrValidAtom = useSetRecoilState(isOrgNumberValid);

  // Organization number duplication check 
  const validateOrgNumber = async orgNumber => {
    if (!orgNumber) {
      setOrgNrErrorMessage("");
      return;
    }
    if (!errors.organizationNumber) {
      try {
        const customerAlreadyExists = await CustomersService.doesCustomerExistByOrgNumber({
          orgNumber
        });
        setTakenOrgNumber(!customerAlreadyExists);
        setOrgNrValidAtom(!customerAlreadyExists);
        setOrgNrErrorMessage(organizationNumberExists);
      } catch (error) {
        console.error(`Error while validating organization number: ${error}`);
      }
    }
  };

  // Phone number validation
  const phoneNr = watch('phoneNumber');
  const contactPhoneNr = watch('contactPhoneNumber');
  const [norwegianOrganization, setNorwegianOrganization] = useState(true);

  // Ensure that the error message doesn't show up on load
  const norwegianOrganizationChanged = useRef(false);
  useEffect(() => {
    if (norwegianOrganizationChanged.current) trigger('organizationNumber');
  }, [norwegianOrganization]);
  const orgNumberErrorMessage = errorMsg => {
    if (orgNumber === null || orgNumber === '') return required;
    return !errorMsg && !takenOrgNumber ? orgNrErrorMessage : errorMsg;
  };
  const orgNumberHelperText = errors.organizationNumber ? norwegianOrganization ? norwegianOrganizationNumber : required : null;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Title, null, t('profile.company_info')), /*#__PURE__*/React.createElement(FormElement, {
    title: t('profile.organization_name'),
    childId: `organizationName_${idSuffix}`
  }, /*#__PURE__*/React.createElement(TextField, {
    inputRef: register({
      required: true
    }),
    name: "organizationName",
    variant: "outlined",
    placeholder: t('profile.organization_name'),
    error: !!errors.organizationName,
    helperText: errors.organizationName ? required : null,
    inputProps: {
      id: `organizationName_${idSuffix}`
    }
  })), /*#__PURE__*/React.createElement(FormElement, {
    title: t('profile.register_location')
  }, /*#__PURE__*/React.createElement(RadioGroup, {
    name: "norwegianOrganizationNumber",
    defaultValue: "norwegian",
    onChange: e => {
      norwegianOrganizationChanged.current = true;
      setNorwegianOrganization(e.target.value === 'norwegian');
    }
  }, /*#__PURE__*/React.createElement(FormControlLabel, {
    value: "norwegian",
    control: /*#__PURE__*/React.createElement(Radio, null),
    label: t('profile.norwegian_organization')
  }), /*#__PURE__*/React.createElement(FormControlLabel, {
    value: "foreign",
    control: /*#__PURE__*/React.createElement(Radio, null),
    label: t('profile.foreign_organization')
  }))), /*#__PURE__*/React.createElement(FormElement, {
    title: t('profile.organization_number'),
    childId: `organizationNumber_${idSuffix}`
  }, /*#__PURE__*/React.createElement(TextField, {
    inputRef: register({
      required: true,
      pattern: norwegianOrganization ? norwegianOrganizationNumberRegex : undefined
    }),
    name: "organizationNumber",
    variant: "outlined",
    placeholder: t('profile.organization_number'),
    error: !!errors.organizationNumber || !takenOrgNumber,
    onBlur: e => validateOrgNumber(e.target.value),
    onChange: () => {
      setTakenOrgNumber(false);
      setOrgNrErrorMessage("");
    },
    helperText: orgNumberErrorMessage(orgNumberHelperText),
    inputProps: {
      id: `organizationNumber_${idSuffix}`
    }
  })), /*#__PURE__*/React.createElement("div", {
    className: textField
  }, /*#__PURE__*/React.createElement(FormElement, {
    title: t('profile.email'),
    childId: `email_${idSuffix}`
  }, /*#__PURE__*/React.createElement(TextField, {
    name: "email",
    variant: "outlined",
    placeholder: t('profile.email'),
    inputRef: register({
      required: true,
      validate: input => !showEmailError(input)
    }),
    error: !!errors.email || showEmailError(email),
    helperText: !!errors.email || showEmailError(email) ? emailFormat : '',
    inputProps: {
      id: `email_${idSuffix}`
    }
  }))), /*#__PURE__*/React.createElement(FormElement, {
    title: t('profile.telephone'),
    childId: `phoneNumber_${idSuffix}`
  }, /*#__PURE__*/React.createElement(Controller, {
    name: "phoneNumber",
    control: control,
    rules: {
      validate: nr => isValidPhoneNumber(nr, true)
    },
    render: field => /*#__PURE__*/React.createElement(MuiTelInput, _extends({}, field, {
      onChange: (_, info) => field.onChange(info.numberValue),
      variant: "outlined",
      defaultCountry: DEFAULT_LANGUAGE,
      langOfCountryName: isNorwegianLangSet ? 'NO' : 'EN',
      fullWidth: true,
      forceCallingCode: true,
      inputProps: {
        id: `phoneNumber_${idSuffix}`,
        maxLength: 17
      },
      InputProps: {
        endAdornment: /*#__PURE__*/React.createElement(InputAdornment, null)
      },
      placeholder: t('profile.telephone'),
      error: showPhoneNumberError(phoneNr),
      helperText: showPhoneNumberError(phoneNr) ? getPhoneNumberError(phoneNr, t) : t('profile.phone_number_info')
    }))
  })), /*#__PURE__*/React.createElement(Title, null, t('profile.contact_info')), /*#__PURE__*/React.createElement(FormElement, {
    title: t('profile.contact_person'),
    childId: `contactName_${idSuffix}`
  }, /*#__PURE__*/React.createElement(TextField, {
    name: "contactName",
    variant: "outlined",
    inputRef: register,
    placeholder: t('profile.contact_person'),
    inputProps: {
      id: `contactName_${idSuffix}`
    },
    InputProps: {
      endAdornment: /*#__PURE__*/React.createElement(InputAdornment, null)
    }
  })), /*#__PURE__*/React.createElement(FormElement, {
    title: t('profile.contact_number'),
    childId: `contactPhoneNumber_${idSuffix}`
  }, /*#__PURE__*/React.createElement(Controller, {
    name: "contactPhoneNumber",
    control: control,
    rules: {
      validate: nr => isValidPhoneNumber(nr, true)
    },
    render: field => /*#__PURE__*/React.createElement(MuiTelInput, _extends({}, field, {
      onChange: (_, info) => field.onChange(info.numberValue),
      variant: "outlined",
      defaultCountry: DEFAULT_LANGUAGE,
      langOfCountryName: isNorwegianLangSet ? 'NO' : 'EN',
      fullWidth: true,
      forceCallingCode: true,
      inputProps: {
        id: `contactPhoneNumber_${idSuffix}`,
        maxLength: 17
      },
      InputProps: {
        endAdornment: /*#__PURE__*/React.createElement(InputAdornment, null)
      },
      placeholder: t('profile.contact_number'),
      error: showPhoneNumberError(contactPhoneNr),
      helperText: showPhoneNumberError(contactPhoneNr) ? getPhoneNumberError(contactPhoneNr, t) : t('profile.phone_number_info')
    }))
  })), /*#__PURE__*/React.createElement("div", {
    className: textField
  }, /*#__PURE__*/React.createElement(FormElement, {
    title: t('profile.contact_email'),
    childId: `contactEmail_${idSuffix}`
  }, /*#__PURE__*/React.createElement(TextField, {
    name: "contactEmail",
    variant: "outlined",
    placeholder: t('profile.contact_email'),
    inputRef: register({
      validate: validateEmail
    }),
    error: !!errors.contactEmail || showEmailError(contactEmail),
    helperText: !!errors.contactEmail || showEmailError(contactEmail) ? emailFormat : '',
    inputProps: {
      id: `contactEmail_${idSuffix}`
    },
    InputProps: {
      endAdornment: /*#__PURE__*/React.createElement(InputAdornment, null)
    }
  }))));
}